import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, Plus, Info, Bell, Monitor, X } from 'lucide-react';
import { toast } from 'sonner';

const AdminSystemMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newMessage, setNewMessage] = useState({
    subject: '',
    message: '',
    sendPushNotification: false,
    displayAsModal: false,
    linkUrl: '',
    platform: 'all'
  });
  const [creating, setCreating] = useState(false);

  // Get admin email from localStorage
  const getAdminEmail = () => {
    const adminData = localStorage.getItem('shootzu_admin');
    if (adminData) {
      try {
        const admin = JSON.parse(adminData);
        return admin.email;
      } catch (e) {
        console.error('Error parsing admin data:', e);
        return null;
      }
    }
    return null;
  };

  const adminEmail = getAdminEmail();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    if (!adminEmail) {
      toast.error('Admin email not found. Please log in again.');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/system-messages`,
        { params: { admin_email: adminEmail } }
      );

      if (response.data.success) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error('Error fetching system messages:', error);
      toast.error(error.response?.data?.detail || 'Failed to load system messages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!adminEmail) {
      toast.error('Admin email not found. Please log in again.');
      return;
    }

    if (!newMessage.subject.trim() || !newMessage.message.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }

    try {
      setCreating(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/system-messages`,
        null,
        {
          params: {
            admin_email: adminEmail,
            subject: newMessage.subject,
            message: newMessage.message,
            send_push_notification: newMessage.sendPushNotification,
            display_as_modal: newMessage.displayAsModal,
            ...(newMessage.linkUrl && newMessage.linkUrl.trim() ? { link_url: newMessage.linkUrl.trim() } : {}),
            platform: newMessage.platform || 'all'
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'System message created successfully');
        setNewMessage({ subject: '', message: '', sendPushNotification: false, displayAsModal: false, linkUrl: '', platform: 'all' });
        setShowCreateModal(false);
        fetchMessages();
      }
    } catch (error) {
      console.error('Error creating system message:', error);
      toast.error(error.response?.data?.detail || 'Failed to create system message');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!adminEmail) {
      toast.error('Admin email not found. Please log in again.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this system message? It will be removed from all users.')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/system-messages/${messageId}`,
        { params: { admin_email: adminEmail } }
      );

      if (response.data.success) {
        toast.success('System message deleted');
        fetchMessages();
      }
    } catch (error) {
      console.error('Error deleting system message:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete system message');
    }
  };

  const handleToggle = async (messageId) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/system-messages/${messageId}/toggle`,
        null,
        { params: { admin_email: adminEmail } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchMessages();
      }
    } catch (error) {
      console.error('Error toggling system message:', error);
      toast.error('Failed to toggle system message');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const closeCreate = () => {
    setShowCreateModal(false);
    setNewMessage({ subject: '', message: '', sendPushNotification: false, displayAsModal: false, linkUrl: '', platform: 'all' });
  };

  return (
    <div className="min-h-full" style={{ background: '#F5F6F9' }}>
      {/* Sticky header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div className="flex items-center gap-[11px]">
          <h1 className="sz-h1" style={{ margin: 0 }}>System Messages</h1>
          <span
            className="sz-mono"
            style={{
              fontFamily: 'Poppins', fontSize: '11.5px', fontWeight: 700,
              color: '#2563EB', background: '#DBEAFE', padding: '4px 10px', borderRadius: '999px'
            }}
          >
            {messages.length} total
          </span>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="sz-btn sz-btn-primary"
        >
          <Plus className="w-4 h-4" strokeWidth={2.2} />
          New Message
        </button>
      </header>

      <main style={{ padding: '22px 30px 56px', maxWidth: '920px' }}>
        {/* Info banner */}
        <div
          className="flex items-start gap-[11px]"
          style={{
            padding: '13px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: '13px', marginBottom: '18px'
          }}
        >
          <Info className="w-[18px] h-[18px] shrink-0 mt-px" style={{ color: '#2563EB' }} />
          <div style={{ fontSize: '12.5px', lineHeight: 1.55, color: '#1E40AF' }}>
            System messages appear in the consumer app for all users. Active messages show as a banner;
            “Display as modal” shows them as a dismissible popup on next app open.
          </div>
        </div>

        {/* Messages list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 rounded-full mx-auto" style={{ borderColor: '#572B7C', borderTopColor: 'transparent' }}></div>
            <p className="mt-4" style={{ color: '#8A90A2' }}>Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="sz-card text-center" style={{ padding: '48px 24px' }}>
            <MessageSquare className="w-14 h-14 mx-auto mb-4" style={{ color: '#A8AEBC' }} />
            <h3 className="sz-h3 mb-1" style={{ fontSize: '16px' }}>No System Messages</h3>
            <p className="mb-5" style={{ color: '#8A90A2', fontSize: '13px' }}>Create your first system message to notify all users</p>
            <button onClick={() => setShowCreateModal(true)} className="sz-btn sz-btn-primary mx-auto">
              <Plus className="w-4 h-4" strokeWidth={2.2} />
              Create Message
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {messages.map((msg) => {
              const active = msg.active;
              return (
                <div
                  key={msg.id}
                  className="sz-card sz-fade"
                  style={{ padding: '18px 20px' }}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1" style={{ minWidth: '200px' }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="sz-h3" style={{ fontSize: '15.5px', margin: 0 }}>{msg.subject}</h3>
                        <span
                          style={{
                            fontFamily: 'Poppins', fontSize: '10px', fontWeight: 700, borderRadius: '999px', padding: '3px 9px',
                            color: active ? '#15803D' : '#475569', background: active ? '#DCFCE7' : '#E2E8F0'
                          }}
                        >
                          {active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                        {msg.display_as_modal && (
                          <span
                            style={{
                              fontFamily: 'Poppins', fontSize: '10px', fontWeight: 700, borderRadius: '999px', padding: '3px 9px',
                              color: '#78287C', background: '#EDE4F4'
                            }}
                          >
                            MODAL
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '13px', lineHeight: 1.55, color: '#2B3142', margin: '10px 0 0', whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                      <div style={{ fontSize: '11.5px', color: '#A8AEBC', marginTop: '11px' }}>
                        Created {formatDate(msg.created_at)} by {msg.created_by} · dismissed by {msg.dismissed_by_count} users
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 items-end shrink-0">
                      <button
                        onClick={() => handleToggle(msg.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 14px',
                          borderRadius: '9px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                          border: `1px solid ${active ? '#FDE68A' : '#BBF7D0'}`,
                          background: active ? '#FFFBEB' : '#F0FDF4',
                          color: active ? '#B45309' : '#15803D'
                        }}
                      >
                        {active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDelete(msg.id)}
                        style={{
                          padding: '8px 14px', border: '1px solid #FECACA', borderRadius: '9px',
                          background: '#fff', color: '#DC2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="sz-modal-backdrop" onClick={closeCreate}>
          <div
            className="sz-modal-card"
            style={{ width: '100%', maxWidth: '500px', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between" style={{ padding: '18px 22px', borderBottom: '1px solid #EEF0F4' }}>
              <h3 className="sz-h3" style={{ fontSize: '16px', margin: 0 }}>New System Message</h3>
              <button
                onClick={closeCreate}
                style={{ width: '30px', height: '30px', border: 'none', borderRadius: '9px', background: '#F1F3F7', color: '#2B3142', cursor: 'pointer' }}
              >
                <X className="w-4 h-4 mx-auto" />
              </button>
            </div>
            <div style={{ padding: '20px 22px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#2B3142', marginBottom: '6px' }}>Subject</label>
              <input
                value={newMessage.subject}
                onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                placeholder="Scheduled maintenance"
                className="sz-input"
                style={{ marginBottom: '14px' }}
              />
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#2B3142', marginBottom: '6px' }}>Message</label>
              <textarea
                value={newMessage.message}
                onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                placeholder="Describe the message shown to users…"
                className="sz-input"
                style={{ height: '90px', resize: 'none', marginBottom: '14px' }}
              />
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#2B3142', marginBottom: '6px' }}>Link URL <span style={{ fontWeight: 500, color: '#A8AEBC' }}>(optional — opens when the push is tapped)</span></label>
              <input
                type="url"
                value={newMessage.linkUrl}
                onChange={(e) => setNewMessage({ ...newMessage, linkUrl: e.target.value })}
                placeholder="https://apps.apple.com/app/shootzu/id6756085766"
                className="sz-input"
                style={{ marginBottom: '14px' }}
              />
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#2B3142', marginBottom: '6px' }}>Send to</label>
              <select
                value={newMessage.platform}
                onChange={(e) => setNewMessage({ ...newMessage, platform: e.target.value })}
                className="sz-input"
                style={{ marginBottom: '14px' }}
              >
                <option value="all">All devices (iOS + Android)</option>
                <option value="ios">iOS only</option>
                <option value="android">Android only</option>
              </select>
              <div className="flex flex-col gap-[10px]" style={{ marginBottom: '6px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13px', color: '#2B3142', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newMessage.sendPushNotification}
                    onChange={(e) => setNewMessage({ ...newMessage, sendPushNotification: e.target.checked })}
                    style={{ width: '16px', height: '16px', accentColor: '#572B7C' }}
                  />
                  <Bell className="w-4 h-4" style={{ color: '#572B7C' }} />
                  Send as push notification
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13px', color: '#2B3142', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newMessage.displayAsModal}
                    onChange={(e) => setNewMessage({ ...newMessage, displayAsModal: e.target.checked })}
                    style={{ width: '16px', height: '16px', accentColor: '#572B7C' }}
                  />
                  <Monitor className="w-4 h-4" style={{ color: '#78287C' }} />
                  Display as modal popup
                </label>
              </div>
            </div>
            <div className="flex gap-[9px]" style={{ padding: '14px 22px', borderTop: '1px solid #EEF0F4' }}>
              <button
                onClick={closeCreate}
                style={{ flex: 1, padding: '11px', border: '1px solid #E0E3EB', borderRadius: '11px', background: '#fff', fontSize: '13px', fontWeight: 600, color: '#2B3142', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !newMessage.subject.trim() || !newMessage.message.trim()}
                style={{
                  flex: 1.4, padding: '11px', border: 'none', borderRadius: '11px', background: '#0C1120',
                  color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  opacity: (creating || !newMessage.subject.trim() || !newMessage.message.trim()) ? 0.5 : 1
                }}
              >
                {creating ? 'Creating...' : 'Create Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSystemMessages;
